import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Layout,
  Card,
  Row,
  Col,
  Typography,
  Button,
  Input,
  Spin,
  Alert,
  Space,
  Tag,
  Badge,
  message,
  Drawer,
  Divider,
} from "antd";
import {
  ShoppingCartOutlined,
  SearchOutlined,
  StarFilled,
} from "@ant-design/icons";
import { useQuery } from '@tanstack/react-query';
import ProductCatalog from "./ProductCatalog";
import ShoppingCart from "./ShoppingCart";
import ProductDetailsModal from "./ProductDetailsModal";
import HeroBanner from "./components/HeroBanner";
import HeroBannerCarousel from "./components/HeroBannerCarousel";
import SocialLinks from "./components/SocialLinks";
import SocialProofNotifications from "./components/SocialProofNotifications";
import TrustBadges from "./components/TrustBadges";
import ProductCardMedia from "./components/ProductCardMedia";
import API_CONFIG from '../config/api.js';
import "./PublicStore.css";

const { Header, Content, Footer } = Layout;
const { Title, Text } = Typography;

interface Store {
  id: number;
  name: string;
  description: string;
  logo_url: string;
  theme_config: any;
}

interface FeaturedProduct {
  id: number;
  name: string;
  online_description: string;
  online_price: number;
  sales_price?: number;
  stock_quantity: number;
  image_url: string;
  gallery_images?: string[];
  category_name: string;
  is_featured?: boolean;
  // 🔄 ÉTAPE 3: Nouveaux champs API pour prix robuste
  final_price?: number;
  has_stock?: boolean;
  stock_status?: 'en_stock' | 'rupture';
  available_for_purchase?: boolean;
  stock_management_mode?: 'standard' | 'pre_order' | 'unlimited';
  // 🆕 Variantes (declinaisons Taille x Couleur)
  is_variant_parent?: boolean;
  variant_attributes?: { name: string; values: string[] }[] | null;
  variants?: any[];
}

const normalizeDisplayText = (value: unknown): string => {
  if (value === null || value === undefined) return "";

  const normalized = String(value).trim();
  return ["null", "undefined"].includes(normalized.toLowerCase())
    ? ""
    : normalized;
};

const StoreFront: React.FC = () => {
  const { storeSlug, productId } = useParams<{ storeSlug: string; productId?: string }>();
  const navigate = useNavigate();

  const [store, setStore] = useState<Store | null>(null);
  const [featuredProducts, setFeaturedProducts] = useState<FeaturedProduct[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [cartVisible, setCartVisible] = useState(false);
  const [cartItemsCount, setCartItemsCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categories, setCategories] = useState<string[]>([]);

  // État pour la modal de détails produit
  const [selectedProduct, setSelectedProduct] =
    useState<FeaturedProduct | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  // État pour la responsivité mobile
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Hook pour détecter la taille d'écran
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 992); // Bootstrap lg breakpoint
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Hook pour lazy loading d'images
  const [imageObserver, setImageObserver] = useState<IntersectionObserver | null>(null);

  useEffect(() => {
    // Créer l'observer pour le lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            const dataSrc = img.getAttribute('data-src');
            if (dataSrc) {
              img.src = dataSrc;
              img.removeAttribute('data-src');
              img.classList.remove('lazy-loading');
              img.classList.add('lazy-loaded');
              observer.unobserve(img);
            }
          }
        });
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.1
      }
    );

    setImageObserver(observer);

    return () => {
      observer.disconnect();
    };
  }, []);

  // URLs sécurisées pour APIs publiques uniquement
  const STORE_API = API_CONFIG.PUBLIC_STORE_API;
  const CUSTOMER_API = API_CONFIG.PUBLIC_CUSTOMER_API;

  // 🚀 CACHE FRONTEND - useQuery pour chargement des données boutique
  const { data: storeData, isLoading, isError } = useQuery({
    queryKey: ['store', storeSlug],
    queryFn: async () => {
      if (!storeSlug) throw new Error('Store slug manquant');

      // 1. Récupérer les infos boutique
      const storeResponse = await fetch(`${STORE_API}/${storeSlug}`);
      if (!storeResponse.ok) {
        throw new Error('Boutique non trouvée');
      }
      const storeInfo = await storeResponse.json();

      // 2. Récupérer les catégories via endpoint léger
      const categoriesResponse = await fetch(
        `${STORE_API}/${storeSlug}/categories`
      );

      // Les produits featured sont déjà renvoyés par GET /api/store/:slug
      const featured: FeaturedProduct[] = (storeInfo.featured_products || []).map(
        (product: any) => ({
          ...product,
          online_description: product.online_description || product.description || "",
        })
      );

      let allCategories: string[] = [];

      if (categoriesResponse.ok) {
        const categoriesData = await categoriesResponse.json();
        allCategories = categoriesData.categories || [];
      } else {
        // Fallback: catégories des produits featured
        allCategories = featured
          .map((p: FeaturedProduct) => p.category_name)
          .filter((c: string, i: number, arr: string[]) => Boolean(c) && arr.indexOf(c) === i);
      }

      return {
        store: storeInfo.store,
        featuredProducts: featured,
        categories: allCategories
      };
    },
    enabled: !!storeSlug,
    staleTime: 5 * 60 * 1000,  // 5 minutes (synchronisé avec cache backend)
  });

  // 🚀 Gestion des erreurs (React Query v5 - remplace onError)
  useEffect(() => {
    if (isError) {
      navigate("/404");
    }
  }, [isError, navigate]);

  // Mettre à jour les états locaux quand les données changent
  useEffect(() => {
    if (storeData) {
      setStore(storeData.store);
      setFeaturedProducts(storeData.featuredProducts);
      setCategories(storeData.categories);
    }
    // Synchroniser le loading state avec isLoading de React Query
    setLoading(isLoading);
  }, [storeData, isLoading]);

  // Hook pour gérer l'ouverture directe d'un produit depuis l'URL
  useEffect(() => {
    if (productId && store && featuredProducts.length > 0) {
      handleDirectProductAccess(productId);
    }
  }, [productId, store, featuredProducts]);

  const handleDirectProductAccess = async (productIdParam: string) => {
    try {
      // D'abord chercher dans les produits featured déjà chargés
      const featuredProduct = featuredProducts.find(p => p.id.toString() === productIdParam);

      if (featuredProduct) {
        // Produit trouvé dans les featured, ouvrir directement
        setSelectedProduct(featuredProduct);
        setModalVisible(true);
        return;
      }

      // Si pas trouvé dans featured, faire un appel API pour récupérer le produit spécifique
      const response = await fetch(`${STORE_API}/${storeSlug}/products/${productIdParam}`);

      if (response.ok) {
        const productData = await response.json();
        const product = productData.product;

        // Adapter la structure pour correspondre à FeaturedProduct.
        // Pour un produit a variantes, la fiche renvoyee par l'API porte
        // is_variant_parent + variants[] + variant_attributes + final_price (mini)
        // et has_stock/available_for_purchase agreges (pas de available_stock propre).
        const isVariantParent = Boolean(product.is_variant_parent);
        const adaptedProduct: FeaturedProduct = {
          id: product.id,
          name: product.name,
          online_description: product.online_description || product.description,
          online_price: product.online_price,
          sales_price: product.sales_price,
          stock_quantity: isVariantParent ? 0 : product.available_stock,
          image_url: product.image_url,
          gallery_images: product.gallery_images,
          category_name: product.category_name,
          is_featured: Boolean(product.online_featured),
          final_price: product.final_price ?? product.online_price,
          has_stock: isVariantParent
            ? Boolean(product.has_stock)
            : product.available_stock > 0,
          stock_status: (isVariantParent
            ? product.available_for_purchase
              ? 'en_stock'
              : 'rupture'
            : product.available_stock > 0
            ? 'en_stock'
            : 'rupture') as 'en_stock' | 'rupture',
          available_for_purchase: isVariantParent
            ? Boolean(product.available_for_purchase)
            : undefined,
          is_variant_parent: isVariantParent,
          variant_attributes: product.variant_attributes ?? null,
          variants: product.variants ?? undefined,
        };

        setSelectedProduct(adaptedProduct);
        setModalVisible(true);
      } else {
        // Produit non trouvé, rediriger vers la boutique principale
        message.error("Produit non trouvé");
        navigate(`/store/${storeSlug}`, { replace: true });
      }
    } catch (error) {
      console.error("Error accessing product directly:", error);
      message.error("Erreur lors de l'accès au produit");
      navigate(`/store/${storeSlug}`, { replace: true });
    }
  };

  // 🚀 FONCTION SUPPRIMÉE - Remplacée par useQuery (cache frontend)
  // L'ancienne fonction fetchStoreData a été migrée vers useQuery ci-dessus pour bénéficier du cache automatique

  const updateCartCount = (count: number) => {
    setCartItemsCount(count);
  };

  // Composant LazyImage optimisé pour StoreFront
  const LazyImage: React.FC<{
    src: string;
    alt: string;
    style: React.CSSProperties;
    placeholder?: string;
  }> = ({ src, alt, style, placeholder = "#f0f0f0" }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
      const img = imgRef.current;
      if (img && imageObserver) {
        img.style.backgroundColor = placeholder;
        img.style.transition = "opacity 0.3s ease, transform 0.3s ease";
        img.style.opacity = "0.7";
        img.style.transform = "scale(1.01)"; // Effet zoom subtil
        
        imageObserver.observe(img);
        
        const handleLoad = () => {
          setIsLoaded(true);
          img.style.opacity = "1";
          img.style.transform = "scale(1)";
          img.style.backgroundColor = "transparent";
        };
        
        const handleError = () => {
          setIsError(true);
          img.style.opacity = "1";
          img.style.transform = "scale(1)";
        };

        img.addEventListener('load', handleLoad);
        img.addEventListener('error', handleError);

        return () => {
          img.removeEventListener('load', handleLoad);
          img.removeEventListener('error', handleError);
          if (imageObserver) {
            imageObserver.unobserve(img);
          }
        };
      }
    }, [imageObserver, placeholder]);

    if (isError) {
      return (
        <div
          style={{
            ...style,
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#ccc",
            fontSize: 12,
          }}
        >
          Image non disponible
        </div>
      );
    }

    return (
      <img
        ref={imgRef}
        data-src={src}
        alt={alt}
        style={style}
        className="lazy-loading"
      />
    );
  };

  // Fonctions pour gérer la modal de détails produit
  const openProductDetails = (product: FeaturedProduct) => {
    setSelectedProduct(product);
    setModalVisible(true);
  };

  const closeProductDetails = () => {
    setModalVisible(false);
    setSelectedProduct(null);

    // Si on est sur une URL de produit spécifique, retourner à la boutique
    if (productId) {
      navigate(`/store/${storeSlug}`, { replace: true });
    }
  };

  const handleModalAddToCart = async (productId: number, quantity: number) => {
    // Réutiliser la logique d'ajout au panier du ProductCatalog
    try {
      const sessionId =
        localStorage.getItem("ecommerce_session_id") ||
        "session_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("ecommerce_session_id", sessionId);

      const response = await fetch(
        `${STORE_API}/${storeSlug}/cart/add`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Session-ID": sessionId,
          },
          body: JSON.stringify({
            product_id: productId,
            quantity,
          }),
        }
      );

      if (response.ok) {
        message.success("Produit ajouté au panier");
        // Mettre à jour le compteur du panier
        const cartResponse = await fetch(
          `${STORE_API}/${storeSlug}/cart`,
          {
            headers: { "X-Session-ID": sessionId },
          }
        );
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          // ✅ FIX: Check if cartData and cartData.items exist before reduce()
          const count = (cartData && cartData.items && Array.isArray(cartData.items))
            ? cartData.items.reduce((sum: number, item: any) => sum + item.quantity, 0)
            : 0;
          updateCartCount(count);
        }
      } else {
        const error = await response.json();
        message.error(error.error || "Erreur lors de l'ajout au panier");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
      message.error("Erreur de connexion");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!store) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <Alert
          message="Boutique non trouvée"
          description="La boutique que vous cherchez n'existe pas ou n'est pas disponible."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const themeConfig = store.theme_config || {};
  const featuredPreview = featuredProducts;
  const primaryColor = themeConfig.primary_color || "#173f5f";
  const secondaryColor = themeConfig.secondary_color || "#e63b3b";
  const headerBackgroundColor =
    themeConfig.header_background_color || primaryColor;
  const headerElementsColor = themeConfig.header_elements_color || "#ffffff";
  const headerHeight = isMobile ? 60 : 72;
  const storeDescription = normalizeDisplayText(store.description);

  const formatStorePrice = (product: FeaturedProduct) => {
    const displayPrice =
      product.final_price || product.online_price || product.sales_price || 0;
    const priceValue = Number(displayPrice);

    if (priceValue === 0) {
      return "Gratuit";
    }

    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(priceValue);
  };

  const renderFeaturedCard = (product: FeaturedProduct) => {
    const isVariantParent = Boolean(product.is_variant_parent);
    // Pour une fiche parent à variantes, la dispo vient des agrégats (pas de stock_quantity propre).
    const inStock = isVariantParent
      ? Boolean(product.available_for_purchase ?? product.has_stock)
      : product.stock_quantity > 0;

    return (
    <Card
      className={`public-store__featured-card ${
        product.is_featured ? "public-store__featured-card--featured" : ""
      }`}
      hoverable={!isMobile}
      onClick={() => openProductDetails(product)}
      cover={
        <ProductCardMedia
          productName={product.name}
          primaryImage={product.image_url}
          galleryImages={product.gallery_images}
          className="public-store__featured-media"
          placeholderClassName="public-store__featured-placeholder"
          onOpen={() => openProductDetails(product)}
        >
          {product.is_featured && (
            <div className="public-store__featured-badge">
              <StarFilled />
              Coup de coeur
            </div>
          )}
        </ProductCardMedia>
      }
      bodyStyle={{ padding: isMobile ? 12 : 16 }}
    >
      <div className="public-store__featured-content">
        <div className="public-store__featured-header">
          <Text className="public-store__featured-title">{product.name}</Text>
        </div>

        <div className="public-store__featured-tags">
          {product.category_name ? <Tag color="blue">{product.category_name}</Tag> : null}
          <Tag color={inStock ? "green" : "default"}>
            {inStock ? "En stock" : "Rupture"}
          </Tag>
        </div>

        <div className="public-store__featured-price">
          {isVariantParent ? "À partir de " : ""}
          {formatStorePrice(product)}
        </div>

        <Button
          type="primary"
          icon={<ShoppingCartOutlined />}
          className="public-store__featured-action"
          disabled={!inStock}
          onClick={(e) => {
            e.stopPropagation();
            // Un produit à variantes n'est jamais ajouté directement : on ouvre la fiche
            // pour choisir la déclinaison (taille/couleur).
            if (isVariantParent) {
              openProductDetails(product);
            } else {
              handleModalAddToCart(product.id, 1);
            }
          }}
        >
          {!inStock
            ? "Rupture"
            : isVariantParent
            ? "Choisir"
            : isMobile
            ? "Ajouter"
            : "Ajouter au panier"}
        </Button>
      </div>
    </Card>
    );
  };

  return (
    <Layout
      className={`public-store public-store--${isMobile ? "mobile" : "desktop"}`}
      style={
        {
          minHeight: "100vh",
          fontFamily: themeConfig.font_family || "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          "--primary-color": primaryColor,
          "--secondary-color": secondaryColor,
          "--header-background-color": headerBackgroundColor,
          "--header-elements-color": headerElementsColor,
          "--font-family": themeConfig.font_family || "Inter",
        } as React.CSSProperties
      }
    >
      {/* Header - Responsive Design */}
      <Header
        className="public-store__header"
        style={{
          backgroundColor: headerBackgroundColor,
          height: headerHeight,
          minHeight: headerHeight,
          padding: isMobile ? "0 1rem" : "0 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Logo et titre - adaptatif */}
        <div className="public-store__brand" style={{ flex: isMobile ? 1 : "none" }}>
          {store.logo_url && (
            <img
              src={store.logo_url}
              alt={store.name}
              className="public-store__brand-logo"
              style={{ height: headerHeight }}
            />
          )}
          <Title 
            level={isMobile ? 4 : 3} 
            className="public-store__brand-title"
            style={{ color: headerElementsColor, margin: 0, fontSize: isMobile ? "16px" : undefined }}
          >
            {store.name}
          </Title>
        </div>

        {/* Section droite - conditionnelle selon la taille d'écran */}
        {!isMobile ? (
          // VERSION DESKTOP - AVEC RÉSEAUX SOCIAUX
          <Space size="large" className="public-store__header-actions">
            {themeConfig.show_search !== false && (
              <Input
                placeholder="Rechercher des produits..."
                prefix={<SearchOutlined />}
                className="public-store__header-search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: 300,
                  color: headerBackgroundColor,
                  backgroundColor: headerElementsColor,
                  borderColor: headerElementsColor,
                }}
              />
            )}

            {/* Réseaux sociaux dans le header desktop */}
            <SocialLinks
              socialLinks={themeConfig.social_links || {}}
              variant="header"
              size="small"
              primaryColor={themeConfig.primary_color || "#173f5f"}
            />

            <Badge count={cartItemsCount} offset={[10, 0]}>
              <Button
                type="text"
                icon={<ShoppingCartOutlined />}
                style={{ color: headerElementsColor }}
                onClick={() => setCartVisible(true)}
              >
                Panier
              </Button>
            </Badge>
          </Space>
        ) : (
          // VERSION MOBILE - Compacte
            <Space size="small" className="public-store__header-actions">
            {themeConfig.show_search !== false && (
              <Button
                type="text"
                icon={<SearchOutlined />}
                style={{ color: headerElementsColor }}
                onClick={() => setMobileMenuVisible(true)}
              />
            )}
            
            <Badge count={cartItemsCount} offset={[5, 0]} size="small">
              <Button
                type="text"
                icon={<ShoppingCartOutlined />}
                style={{ color: headerElementsColor, padding: "4px 8px" }}
                onClick={() => setCartVisible(true)}
              />
            </Badge>
          </Space>
        )}
      </Header>

      {/* Drawer mobile pour la recherche */}
      <Drawer
        className="public-store__search-drawer"
        title="Rechercher des produits"
        placement="top"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        height="auto"
        style={{ zIndex: 1001 }}
      >
        <Input
          placeholder="Rechercher des produits..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="large"
          autoFocus
          onPressEnter={() => setMobileMenuVisible(false)}
        />

        {/* Réseaux sociaux dans le menu mobile */}
        <div style={{ marginTop: 20, borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: "#666" }}>
            Suivez-nous
          </div>
          <SocialLinks
            socialLinks={themeConfig.social_links || {}}
            variant="footer"
            size="medium"
            showLabels={true}
            primaryColor={themeConfig.primary_color || "#173f5f"}
          />
        </div>

        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Button
            type="primary"
            onClick={() => setMobileMenuVisible(false)}
            style={{ minWidth: 120 }}
          >
            Rechercher
          </Button>
        </div>
      </Drawer>

      {/* Hero Banner - Carrousel ou Simple selon configuration */}
      {(() => {
        // Nouveau système: bannières multiples
        if (themeConfig.banner_images && themeConfig.banner_images.length > 0) {
          return (
            <HeroBannerCarousel
              bannerImages={themeConfig.banner_images}
              storeName={store.name}
              storeDescription={storeDescription}
              primaryColor={themeConfig.primary_color || "#173f5f"}
              secondaryColor={themeConfig.secondary_color || "#e63b3b"}
              fontFamily={themeConfig.font_family || "Inter"}
              carouselSettings={themeConfig.carousel_settings}
              onShopNow={() => {
                // Défiler vers les produits
                const productsSection = document.getElementById("products-section");
                if (productsSection) {
                  productsSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
              onExplore={() => {
                // Défiler vers le catalogue
                const catalogSection = document.getElementById("catalog-section");
                if (catalogSection) {
                  catalogSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
            />
          );
        }

        // Rétrocompatibilité: banner_image unique
        if (themeConfig.banner_image) {
          return (
            <HeroBanner
              bannerImage={themeConfig.banner_image}
              storeName={store.name}
              storeDescription={storeDescription}
              primaryColor={themeConfig.primary_color || "#173f5f"}
              secondaryColor={themeConfig.secondary_color || "#e63b3b"}
              fontFamily={themeConfig.font_family || "Inter"}
              onShopNow={() => {
                // Défiler vers les produits
                const productsSection = document.getElementById("products-section");
                if (productsSection) {
                  productsSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
              onExplore={() => {
                // Défiler vers le catalogue
                const catalogSection = document.getElementById("catalog-section");
                if (catalogSection) {
                  catalogSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
            />
          );
        }

        return null; // Pas de bannière
      })()}

      {/* Produits Featured de la page d'accueil.
          Masqués dès qu'une recherche ou un filtre catégorie est actif : dans ce cas
          le catalogue ci-dessous inclut TOUS les produits (featured compris) afin que
          la recherche couvre les deux sections sans doublon. */}
      {featuredProducts.length > 0 && !searchQuery.trim() && !selectedCategory && (
        <Content
          id="products-section"
          className="public-store__section public-store__section--featured"
          style={{ padding: isMobile ? "1rem" : "2rem" }}
        >
          <div>
            <div className="public-store__section-head">
              <Title level={2} className="public-store__section-title">
              <StarFilled style={{ color: "#faad14", marginRight: 8 }} />
              Nos Coups de Cœur
            </Title>
            <Text
              type="secondary"
              style={{
                display: "block",
                textAlign: "center",
                marginBottom: "2rem",
              }}
            >
              Découvrez notre sélection de produits mis en avant
            </Text>

            </div>

            {isMobile ? (
              <div className="public-store__featured-scroll">
                {featuredPreview.map((product) => (
                  <div key={product.id} className="public-store__featured-scroll-item">
                    {renderFeaturedCard(product)}
                  </div>
                ))}
              </div>
            ) : (
              <Row gutter={[20, 20]}>
                {featuredPreview.map((product) => (
                  <Col key={product.id} xs={24} sm={12} md={12} lg={6}>
                    {renderFeaturedCard(product)}
                  </Col>
                ))}
              </Row>
            )}

            {false && <Row gutter={[16, 16]}>
              {featuredProducts.map((product) => (
                <Col key={product.id} xs={24} sm={12} md={8} lg={6}>
                  <Badge.Ribbon
                    text={
                      <span>
                        <StarFilled style={{ marginRight: 4 }} />
                        {product.is_featured ? "Featured" : "Populaire"}
                      </span>
                    }
                    color={product.is_featured ? "gold" : "blue"}
                    style={{
                      display: product.is_featured ? "block" : "none",
                    }}
                  >
                    <Card
                      hoverable
                      onClick={() => openProductDetails(product)}
                      style={{
                        height: "100%",
                        cursor: "pointer",
                        // ⭐ Bordure spéciale pour les vrais featured
                        border: product.is_featured
                          ? "2px solid #faad14"
                          : "1px solid #f0f0f0",
                        boxShadow: product.is_featured
                          ? "0 6px 16px rgba(250, 173, 20, 0.2)"
                          : "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "all 0.3s ease",
                        transform: "translateY(0)",
                      }}
                      onMouseEnter={(e) => {
                        if (!isMobile) {
                          (e.currentTarget as HTMLElement).style.transform = "translateY(-6px)";
                          (e.currentTarget as HTMLElement).style.boxShadow = product.is_featured
                            ? "0 12px 30px rgba(250, 173, 20, 0.3)"
                            : "0 6px 20px rgba(0,0,0,0.15)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isMobile) {
                          (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
                          (e.currentTarget as HTMLElement).style.boxShadow = product.is_featured
                            ? "0 6px 16px rgba(250, 173, 20, 0.2)"
                            : "0 2px 8px rgba(0,0,0,0.1)";
                        }
                      }}
                      cover={
                        <div style={{ position: "relative" }}>
                          {product.image_url ? (
                            <LazyImage
                              src={product.image_url}
                              alt={product.name}
                              style={{
                                height: isMobile ? 150 : 200, // Images plus petites sur mobile
                                objectFit: "cover",
                                width: "100%",
                              }}
                              placeholder={product.is_featured ? "#fff7e6" : "#f0f0f0"}
                            />
                          ) : (
                            <div
                              style={{
                                height: isMobile ? 150 : 200, // Même hauteur pour placeholder
                                backgroundColor: "#f0f0f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#ccc",
                              }}
                            >
                              Pas d'image
                            </div>
                          )}

                          {/* ⭐ Badge sur l'image pour les vrais featured */}
                          {product.is_featured && (
                            <div
                              style={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                background:
                                  "linear-gradient(45deg, #faad14, #ffd53e)",
                                color: "white",
                                padding: "6px 12px",
                                borderRadius: "15px",
                                fontSize: "11px",
                                fontWeight: "bold",
                                display: "flex",
                                alignItems: "center",
                                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                                zIndex: 1,
                              }}
                            >
                              <StarFilled
                                style={{ marginRight: 4, fontSize: "10px" }}
                              />
                              COUP DE CŒUR
                            </div>
                          )}
                        </div>
                      }
                      actions={[
                        <Space
                          key="actions"
                          direction="vertical"
                          style={{ width: "100%" }}
                          size={isMobile ? "small" : "middle"}
                        >
                          <Button
                            type={product.is_featured ? "primary" : "default"}
                            icon={<ShoppingCartOutlined />}
                            size={isMobile ? "large" : "middle"}
                            style={{
                              width: "100%",
                              background: product.is_featured
                                ? "linear-gradient(45deg, #173f5f, #295d86)"
                                : undefined,
                            }}
                            disabled={product.stock_quantity === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleModalAddToCart(product.id, 1);
                            }}
                          >
                            {product.stock_quantity === 0
                              ? "Rupture"
                              : isMobile
                              ? "Ajouter"
                              : "Ajouter au panier"}
                          </Button>
                        </Space>,
                      ]}
                    >
                      <Card.Meta
                        title={
                          <div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: "bold",
                                color: product.is_featured
                                  ? "#faad14"
                                  : "inherit",
                                lineHeight: "1.2",
                              }}
                            >
                              {product.is_featured && (
                                <StarFilled
                                  style={{ marginRight: 6, color: "#faad14" }}
                                />
                              )}
                              {product.name}
                            </div>
                            <div style={{ marginTop: 6 }}>
                              <Tag color="blue">{product.category_name}</Tag>
                              {product.stock_quantity > 0 && (
                                <Tag color="green">En stock</Tag>
                              )}
                              {/* ⭐ Tag spécial pour featured */}
                              {product.is_featured && (
                                <Tag
                                  color="gold"
                                  style={{
                                    fontWeight: "bold",
                                    background:
                                      "linear-gradient(45deg, #faad14, #ffd53e)",
                                    border: "none",
                                    color: "white",
                                    fontSize: "10px",
                                  }}
                                >
                                  ⭐ FEATURED
                                </Tag>
                              )}
                            </div>
                          </div>
                        }
                        description={
                          <div>
                            <div
                              style={{
                                fontSize: 16,
                                fontWeight: "bold",
                                color: product.is_featured
                                  ? "#faad14"
                                  : "#52c41a",
                                marginTop: 8,
                              }}
                            >
                              {(() => {
                                // 🔄 ÉTAPE 3: Utilisation final_price + fallbacks intelligents
                                const displayPrice = product.final_price || product.online_price || product.sales_price || 0;
                                const priceValue = Number(displayPrice);

                                if (priceValue === 0) {
                                  return <span style={{ color: "#52c41a", fontWeight: "bold" }}>Gratuit</span>;
                                }

                                return new Intl.NumberFormat("fr-FR", {
                                  style: "currency",
                                  currency: "XOF",
                                }).format(priceValue);
                              })()}
                            </div>
                          </div>
                        }
                      />
                    </Card>
                  </Badge.Ribbon>
                </Col>
              ))}
            </Row>}
          </div>
        </Content>
      )}

      <Content
        id="catalog-section"
        className="public-store__section"
        style={{ padding: isMobile ? "1rem" : "2rem" }}
      >
        {themeConfig.show_categories !== false && categories.length > 0 && (
          <Card className="public-store__categories-card">
            <div className="public-store__categories-list">
              <Button
                className="public-store__category-chip"
                type={selectedCategory === "" ? "primary" : "default"}
                onClick={() => setSelectedCategory("")}
              >
                Toutes les catégories
              </Button>
              {Array.from(new Set(categories)).map((category) => (
                <Button
                  key={category}
                  className="public-store__category-chip"
                  type={selectedCategory === category ? "primary" : "default"}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </Button>
              ))}
            </div>
          </Card>
        )}

        <ProductCatalog
          storeSlug={storeSlug!}
          searchQuery={searchQuery}
          selectedCategory={selectedCategory}
          productsPerPage={themeConfig.products_per_page || 12}
          onCartUpdate={updateCartCount}
        />
      </Content>

      {/* Footer Moderne avec config personnalisée */}
      <Footer
        className="public-store__footer"
        style={{
          background: `linear-gradient(135deg, ${themeConfig.primary_color || "#173f5f"}08, ${themeConfig.secondary_color || "#e63b3b"}08)`,
          borderTop: `1px solid ${themeConfig.primary_color || "#173f5f"}20`,
          textAlign: "center",
          padding: isMobile ? "28px 16px 20px" : "40px 20px 20px",
          fontFamily: themeConfig.font_family || "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        }}
      >
        <div className="public-store__footer-inner">
          {/* Badges de confiance */}
          <div className="public-store__footer-trust">
            <TrustBadges
              satisfiedCustomers={500}
              variant="default"
              compact={isMobile}
            />
          </div>

          <Divider className="public-store__footer-divider" />

          {/* Réseaux sociaux */}
          {Object.values(themeConfig.social_links || {}).some(
            (link: any) => typeof link === "string" && link.trim()
          ) && (
            <div className="public-store__footer-social">
              <Text className="public-store__footer-social-title">
                Rejoignez la boutique
              </Text>
              <SocialLinks
                socialLinks={themeConfig.social_links || {}}
                variant="footer"
                size="large"
                primaryColor={themeConfig.primary_color || "#173f5f"}
              />
            </div>
          )}

          {/* Texte personnalisé du footer + marque ELSA GESTION */}
          <div className="public-store__footer-meta">
            {/* Texte personnalisé si configuré */}
            {themeConfig.footer_text && (
              <Text
                type="secondary"
                className="public-store__footer-text"
              >
                {themeConfig.footer_text}
              </Text>
            )}

            {/* Copyright et marque ELSA GESTION - TOUJOURS AFFICHÉ */}
            <Text
              type="secondary"
              className={`public-store__footer-copy${
                themeConfig.footer_text ? "" : " public-store__footer-copy--strong"
              }`}
            >
              © {new Date().getFullYear()} {store.name}. Propulsé par{" "}
              <a
                href="https://www.elsagestion.com"
                target="_blank"
                rel="noopener noreferrer"
                className="public-store__footer-brand-link"
                style={{
                  background: `linear-gradient(90deg, ${themeConfig.primary_color || "#173f5f"}, ${themeConfig.secondary_color || "#e63b3b"})`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                ELSA GESTION
              </a>
              .
            </Text>

            {/* Ligne décorative */}
            <div
              className="public-store__footer-accent"
              style={{
                background: `linear-gradient(90deg, ${themeConfig.primary_color || "#173f5f"}, ${themeConfig.secondary_color || "#e63b3b"})`,
              }}
            />
          </div>
        </div>
      </Footer>

      {/* Shopping Cart */}
      <ShoppingCart
        visible={cartVisible}
        onClose={() => setCartVisible(false)}
        storeSlug={storeSlug!}
        onCartUpdate={updateCartCount}
      />

      {/* Modal de détails produit */}
      <ProductDetailsModal
        visible={modalVisible}
        product={selectedProduct}
        onClose={closeProductDetails}
        onAddToCart={handleModalAddToCart}
        storeSlug={storeSlug!}
      />

      {/* Notifications de preuves sociales (popups) */}
      <SocialProofNotifications
        storeSlug={storeSlug!}
        position="bottom-left"
        displayInterval={12000}
        displayDuration={6000}
        refreshInterval={30000}
        enabled={true}
      />
    </Layout>
  );
};

export default StoreFront;
